import { Injectable, effect } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { AuthenticationService } from 'src/app/auth/authentication-service/authentication.service';
import { RemoteDataService } from '../remote-data-service/remote-data.service';
import { TagCachePayload } from 'src/app/cache/tag-cache-payload/tag-cache.payload';
import { StoreService } from '../store-service/store.service';
import { ProjectCachePayload } from 'src/app/cache/project-cache-payload/project-cache.payload';
import { EntityInterface } from 'src/app/entities/interfaces/entity-interface';
import { CachePayloadInterface } from 'src/app/cache/interfaces/cache.payload-interface';
import { ClientCachePayload } from 'src/app/cache/client-cache-payload/client-cache.payload';
import { TimeTrackCachePayload } from 'src/app/cache/time-track-cache-payload/time-track-cache.payload';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { UserAuthenticationDataManagerService } from 'src/app/auth/user-authentication-data-manager-service/user-authentication-data-manager.service';
import { FiltersService } from '../filters-service/filters.service';
import log from 'loglevel';
import { UserCachePayload } from 'src/app/cache/user-cache-payload/user-cache.payload';
import { UserRepository } from '../remote-data-service/repositories/user-repository/user.repository';

/**
 * Service responsible for managing application-wide actions and data streams.
 * Handles authentication state, data initialization and caching of entities.
 */
@Injectable({
  providedIn: 'root',
})
export class ActionsService {
  /**
   * Observable streams for different entity types.
   * Each stream maintains its own cache payload to store and manage entity data.
   */
  protected _tags$?: Observable<TagCachePayload>;
  protected _projects$?: Observable<ProjectCachePayload>;
  protected _clients$?: Observable<ProjectCachePayload>;
  protected _timeTracks$?: Observable<TimeTrackCachePayload>;
  protected _users$?: Observable<UserCachePayload>;

  /** Logger instance for debugging and tracing */
  protected logger = log.getLogger('ActionService');

  constructor(
    protected authService: AuthenticationService,
    protected remoteDataService: RemoteDataService,
    protected storeService: StoreService,
    protected router: Router,
    protected userAuth: UserAuthenticationDataManagerService,
    protected filters: FiltersService,
    protected userRep: UserRepository
  ) {
    /**
     * Effect that runs on every page navigation and auth state change.
     * Handles:
     * - Token expiration and refresh
     * - Route protection (redirects to login/tracker based on auth state)
     * - Data stream reinitialization when route changes
     */
    effect(() => {
      const authData = this.userAuth.retrieve();

      if (this.authService.loggedInStatus() && authData) {
        this.authService.checkTokenExpiration(authData.exp);
        this.initializeAllStream()
        this.userRep.getCurrentUser().subscribe((user) => {
          this.storeService.saveCurrentUser(user)
        });
      }

      if (this.authService.loggedInStatus() && this.router.url === '/login') {
        this.logger.debug('User is logged in, redirecting to tracker page.');
        this.router.navigate(['/tracker-page']);
      }

      if (!this.authService.loggedInStatus()) {
        this.logger.debug('User is not logged in, redirecting to login page.');
        this.router.navigate(['/login']);
      }

    });

    // Subscribe to router navigation events to reinitialize data streams
    // This ensures that filters and data are updated when the route changes
    //router.events
    //  .pipe(
    //    filter(event => event instanceof NavigationEnd)
    //  )
    //  .subscribe(() => {
    //    this.initializeAllStream();
    //  })

    //Initial data stream initialization
    //this.initializeAllStream()
  }


  /**
   * Initializes all data streams for the application.
   * This includes tags, projects, clients and time tracks.
   * Each stream is initialized with its corresponding filters based on the current route.
   */
  initializeAllStream() {
    // Initialize tags stream with route-specific filters
    // Each route may have different filter requirements (e.g., tracker page vs reports)
    // Filters are managed by FiltersService and retrieved based on current URL
    this._tags$ = this.initializeStream(
      this.remoteDataService.allTags(this.filters.tagsFilters().get(
        this.router.url
      ) || undefined),
      new TagCachePayload([]),
      'Tag'
    ) as Observable<TagCachePayload>;
    this.logger.debug('ActionsService: Tags stream initialized.');

    // Initialize projects stream with route-specific filters
    // Projects may be filtered differently based on the current view/context
    this._projects$ = this.initializeStream(
      this.remoteDataService.allProjects(this.filters.projectsFilters().get(
        this.router.url
      ) || undefined),
      new ProjectCachePayload([]),
      'Project'
    ) as Observable<ProjectCachePayload>;

    this.logger.debug('ActionsService: Projects stream initialized.');

    // Initialize clients stream with route-specific filters
    // Client data visibility may vary based on user permissions and current route
    this._clients$ = this.initializeStream(
      this.remoteDataService.allClients(this.filters.clientsFilters().get(
        this.router.url
      ) || undefined),
      new ClientCachePayload([]),
      'Client'
    ) as Observable<ClientCachePayload>;

    this.logger.debug('ActionsService: clients stream initialized.');

    // Initialize time tracks stream with route-specific filters
    // Time tracks are filtered based on:
    // - Current route requirements (e.g., daily view vs monthly reports)
    // - User permissions
    // - Selected date ranges or other view-specific criteria
    this._timeTracks$ = this.initializeStream(
      this.remoteDataService.allTimeTracks(this.filters.timeTracksFilters()
        .get(this.router.url) || undefined
      ),
      new TimeTrackCachePayload([]),
      'TimeTrack'
    ) as Observable<TimeTrackCachePayload>;

    this.logger.debug('ActionsService: time tracks stream initialized.');

    // Initialize users stream with route-specific filters
    // Users data may be filtered based on roles, groups, and permissions
    this._users$ = this.initializeStream(
      this.remoteDataService.getUsers(this.filters.userFilters()
        .get(this.router.url) || undefined),
      new UserCachePayload([]),
      'User'
    ) as Observable<UserCachePayload>;

    this.logger.debug('ActionsService: users stream initialized.');
  }


  /**
   * Initializes a data stream for a given entity type.
   * Creates an observable that manages the entity data and its cache.
   * 
   * @param {Observable<EntityInterface[]>} obs - Source observable for entity data
   * @param {CachePayloadInterface} cachePayload - Cache container for the entities
   * @returns {Observable<CachePayloadInterface>} Stream that emits cached entity data
   */
  protected initializeStream(
    obs: Observable<EntityInterface[]>,
    cachePayload: CachePayloadInterface,
    entityType: string
  ): Observable<CachePayloadInterface> {
    let data$: Observable<CachePayloadInterface> | null = null;

    data$ = obs.pipe(
      map((entities) => {
        cachePayload.payload = entities;
        return cachePayload;
      })
    );

    data$.subscribe((cachePayload: CachePayloadInterface) => {
      this.logger.debug('ActionsService: New Entity payload received.');
      this.logger.trace('ActionsService: Received payload: ', cachePayload);
      this.storeService.saveEntities(cachePayload.payload, entityType);
    });

    return data$;
  }

  /**
   * Adds a new time track entry to the store.
   * Updates the application state with the new time track data.
   * 
   * @param {TimeTrack} timeTrack - The new time track entry to add
   */
  public addTimeTrack(timeTrack: TimeTrack): void {
    this.logger.debug('ACTION addTimeTrack', timeTrack);
    this.storeService.saveTimeTracks([timeTrack]);
  }
}
